import { useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { users, ApiError } from '../lib/api';
import type { User } from '../lib/api';
import Layout from '../components/Layout';

export default function UserManagement() {
  const queryClient = useQueryClient();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user' | 'viewer'>('user');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: userList, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: users.list,
  });

  const inviteMutation = useMutation({
    mutationFn: () => users.invite(inviteEmail, inviteRole),
    onSuccess: () => {
      setSuccess('招待メールを送信しました');
      setIsInviteModalOpen(false);
      setInviteEmail('');
      setInviteRole('user');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('招待に失敗しました');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; updates: Partial<User> }) =>
      users.update(data.id, data.updates),
    onSuccess: () => {
      setSuccess('ユーザーを更新しました');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('更新に失敗しました');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => users.delete(id),
    onSuccess: () => {
      setSuccess('ユーザーを無効化しました');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('無効化に失敗しました');
      }
    },
  });

  const handleInvite = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    inviteMutation.mutate();
  };

  const handleUpdate = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setError('');
    updateMutation.mutate({
      id: selectedUser.id,
      updates: {
        name: selectedUser.name,
        role: selectedUser.role,
        status: selectedUser.status,
      },
    });
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      admin: 'bg-jarvis-gold text-jarvis-navy-dark',
      user: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    const labels: Record<string, string> = {
      admin: '管理者',
      user: 'ユーザー',
      viewer: '閲覧者',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[role] || styles.viewer}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      disabled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    const labels: Record<string, string> = {
      active: '有効',
      pending: '保留中',
      disabled: '無効',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.disabled}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* ヘッダー */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              ユーザー管理
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              システムのユーザーを管理します
            </p>
          </div>
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            ➕ ユーザー招待
          </button>
        </div>

        {/* アラート */}
        {success && (
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600">
            {success}
          </div>
        )}
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500">
            {error}
          </div>
        )}

        {/* ユーザー一覧 */}
        <div className="card">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      ユーザー
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      ロール
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      ステータス
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      最終ログイン
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      アクション
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userList?.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-jarvis-gold text-jarvis-navy-dark flex items-center justify-center font-bold">
                            {u.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {u.name}
                              {u.isMaster && (
                                <span className="ml-2 px-2 py-0.5 text-xs rounded bg-gradient-to-r from-jarvis-gold to-jarvis-gold-light text-jarvis-navy-dark font-bold">
                                  MASTER
                                </span>
                              )}
                            </p>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {u.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getRoleBadge(u.role)}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(u.status)}
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {u.lastLoginAt
                          ? new Date(u.lastLoginAt).toLocaleDateString('ja-JP')
                          : '未ログイン'}
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        {!u.isMaster && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setIsEditModalOpen(true);
                              }}
                              className="text-sm text-jarvis-gold hover:underline"
                            >
                              編集
                            </button>
                            {u.status !== 'disabled' && (
                              <button
                                onClick={() => {
                                  if (confirm(`${u.name}を無効化しますか？`)) {
                                    deleteMutation.mutate(u.id);
                                  }
                                }}
                                className="text-sm text-red-500 hover:underline"
                              >
                                無効化
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 招待モーダル */}
        {isInviteModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="card w-full max-w-md mx-4">
              <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                ユーザー招待
              </h2>
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    メールアドレス *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    ロール *
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'admin' | 'user' | 'viewer')}
                    className="input"
                  >
                    <option value="user">ユーザー</option>
                    <option value="admin">管理者</option>
                    <option value="viewer">閲覧者</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsInviteModalOpen(false)}
                    className="btn-outline"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={inviteMutation.isPending}
                    className="btn-primary"
                  >
                    {inviteMutation.isPending ? '送信中...' : '招待を送信'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 編集モーダル */}
        {isEditModalOpen && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="card w-full max-w-md mx-4">
              <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                ユーザー編集
              </h2>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    名前 *
                  </label>
                  <input
                    type="text"
                    value={selectedUser.name}
                    onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    ロール *
                  </label>
                  <select
                    value={selectedUser.role}
                    onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value as 'admin' | 'user' | 'viewer' })}
                    className="input"
                  >
                    <option value="user">ユーザー</option>
                    <option value="admin">管理者</option>
                    <option value="viewer">閲覧者</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    ステータス *
                  </label>
                  <select
                    value={selectedUser.status}
                    onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value as 'active' | 'pending' | 'disabled' })}
                    className="input"
                  >
                    <option value="active">有効</option>
                    <option value="pending">保留中</option>
                    <option value="disabled">無効</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedUser(null);
                    }}
                    className="btn-outline"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="btn-primary"
                  >
                    {updateMutation.isPending ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

